// ABOUTME: Context for managing rebalance queue state
// ABOUTME: Stores symbols marked for rebalancing with localStorage persistence

import { createContext, type ReactNode, useCallback, useContext, useMemo, useState } from 'react';
import { safeGetJson, safeRemove, safeSetJson } from '../lib/storage';

export interface RebalanceItem {
  symbol: string;
  signal: string;
  alphaScore: number;
  addedAt: string;
}

interface RebalanceQueueContextValue {
  queue: RebalanceItem[];
  addToQueue: (item: Omit<RebalanceItem, 'addedAt'>) => void;
  removeFromQueue: (symbol: string) => void;
  clearQueue: () => void;
  isInQueue: (symbol: string) => boolean;
}

const RebalanceQueueContext = createContext<RebalanceQueueContextValue | null>(null);

const QUEUE_STORAGE_KEY = 'alpha-finder-rebalance-queue';

function loadQueue(): RebalanceItem[] {
  return safeGetJson<RebalanceItem[]>(QUEUE_STORAGE_KEY) ?? [];
}

function saveQueue(queue: RebalanceItem[]): void {
  safeSetJson(QUEUE_STORAGE_KEY, queue);
}

export function RebalanceQueueProvider({ children }: { children: ReactNode }) {
  const [queue, setQueue] = useState<RebalanceItem[]>(loadQueue);

  const addToQueue = useCallback((item: Omit<RebalanceItem, 'addedAt'>) => {
    setQueue((prev) => {
      // Don't add duplicates
      if (prev.some((i) => i.symbol === item.symbol)) {
        return prev;
      }
      const updated = [...prev, { ...item, addedAt: new Date().toISOString() }];
      saveQueue(updated);
      return updated;
    });
  }, []);

  const removeFromQueue = useCallback((symbol: string) => {
    setQueue((prev) => {
      const updated = prev.filter((i) => i.symbol !== symbol);
      saveQueue(updated);
      return updated;
    });
  }, []);

  const clearQueue = useCallback(() => {
    setQueue([]);
    safeRemove(QUEUE_STORAGE_KEY);
  }, []);

  const isInQueue = useCallback(
    (symbol: string) => queue.some((i) => i.symbol === symbol),
    [queue]
  );

  const value = useMemo(
    () => ({ queue, addToQueue, removeFromQueue, clearQueue, isInQueue }),
    [queue, addToQueue, removeFromQueue, clearQueue, isInQueue]
  );

  return <RebalanceQueueContext.Provider value={value}>{children}</RebalanceQueueContext.Provider>;
}

export function useRebalanceQueue() {
  const context = useContext(RebalanceQueueContext);
  if (!context) {
    throw new Error('useRebalanceQueue must be used within a RebalanceQueueProvider');
  }
  return context;
}
